import { Controller } from "@hotwired/stimulus"

// Drag left/right to move between questions on the test-practice screen.
//
// Attached to the `test-results-form` turbo-frame. The question content follows
// the finger during the swipe; on release it either completes (slides the rest
// of the way out, navigates, and the new question slides in) or springs back.
// Direction maps to the navigator's Back/Next controls, tagged
// `[data-nav-role="prev"]` / `[data-nav-role="next"]`:
//   - active/answering form: next = the submit button (disabled until answered →
//     forward rubber-bands until an answer is picked); prev = Back link.
//   - reviewing form: prev/next are the Back/Next links.
// Navigation is a real `el.click()`, so it behaves exactly like tapping the
// control. A single click listener records the travel direction for BOTH drags
// and Back/Next/cell taps, so taps get the same slide-in (from the right/left as
// the progression dictates). Only elements tagged `data-swipe-content` (the
// question body) move; the navigator and the fixed progress strip stay anchored.
// The image (`image-zoom`) is intentionally NOT ignored: the gesture is
// disambiguated by movement, not target, so a horizontal drag across the image
// swipes while a tap still opens the zoom overlay (no INTENT_PX travel → no
// preventDefault → the click fires) and a two-finger pinch bails on the
// single-touch guard in handleTouchStart. `data-swipe-ignore` opts a region out
// (the Ask AI bottom sheet, which sits inside the swiped content).
const DRAG_IGNORE = '[data-controller~="scroll"], [data-swipe-ignore], input, textarea, select'
const INTENT_PX = 10 // movement before committing to horizontal vs vertical
const COMMIT_PX = 80 // drag distance that triggers navigation
const FLICK_VELOCITY = 0.5 // px/ms — a fast flick commits at a shorter distance
const FLICK_MIN_PX = 28
const RUBBER = 0.3 // resistance when dragging toward a missing neighbour
const RUBBER_CAP = 64
const FADE_OVER_PX = 360 // distance over which content fades while dragging
const SLIDE_IN_PX = 96 // how far the incoming question travels
const SLIDE_IN_MS = 240
const OUT_PX = 160 // how far the outgoing question travels before navigating
const OUT_MS = 240
const SNAP_MS = 280
const EASE_OUT = "cubic-bezier(0.22, 1, 0.36, 1)"

export default class extends Controller {
  connect() {
    this.navigating = false
    this.direction = null // 'next' | 'prev', set per navigation by handleClick
    this.fromDrag = false // true only between commitDrag and its synthetic click
    this.resetDrag()

    this.boundTouchStart = this.handleTouchStart.bind(this)
    this.boundTouchMove = this.handleTouchMove.bind(this)
    this.boundTouchEnd = this.handleTouchEnd.bind(this)
    this.boundTouchCancel = this.handleTouchCancel.bind(this)
    this.boundClick = this.handleClick.bind(this)
    this.boundFrameRender = this.handleFrameRender.bind(this)
    this.boundFrameLoad = this.handleFrameLoad.bind(this)

    this.element.addEventListener("touchstart", this.boundTouchStart, { passive: true })
    // Non-passive so we can claim the horizontal gesture (preventDefault) once
    // a drag is underway, without blocking vertical scroll before that.
    this.element.addEventListener("touchmove", this.boundTouchMove, { passive: false })
    this.element.addEventListener("touchend", this.boundTouchEnd, { passive: true })
    this.element.addEventListener("touchcancel", this.boundTouchCancel, { passive: true })
    this.element.addEventListener("click", this.boundClick)
    this.element.addEventListener("turbo:frame-render", this.boundFrameRender)
    this.element.addEventListener("turbo:frame-load", this.boundFrameLoad)
  }

  disconnect() {
    this.element.removeEventListener("touchstart", this.boundTouchStart)
    this.element.removeEventListener("touchmove", this.boundTouchMove)
    this.element.removeEventListener("touchend", this.boundTouchEnd)
    this.element.removeEventListener("touchcancel", this.boundTouchCancel)
    this.element.removeEventListener("click", this.boundClick)
    this.element.removeEventListener("turbo:frame-render", this.boundFrameRender)
    this.element.removeEventListener("turbo:frame-load", this.boundFrameLoad)
    if (this.navigateTimeout) clearTimeout(this.navigateTimeout)
  }

  resetDrag() {
    this.tracking = false
    this.dragging = false
    this.dragEls = null
    this.hasPrev = false
    this.hasNext = false
  }

  get reduceMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  get isRtl() {
    return getComputedStyle(this.element).direction === "rtl"
  }

  // The question content that slides — tagged `data-swipe-content`. The navigator
  // (Back / pager / Next + hairline) and the fixed progress bar stay anchored.
  // Falls back to all non-fixed children if nothing is tagged.
  contentElements() {
    const tagged = Array.from(this.element.querySelectorAll("[data-swipe-content]"))
    if (tagged.length) return tagged
    return Array.from(this.element.children).filter(
      (el) => getComputedStyle(el).position !== "fixed"
    )
  }

  // LTR: dragging left → next, right → previous. RTL flips it.
  wantsNext(deltaX) {
    return deltaX < 0 ? !this.isRtl : this.isRtl
  }

  handleTouchStart(event) {
    if (this.navigating || event.touches.length !== 1 || event.target.closest(DRAG_IGNORE)) {
      this.resetDrag()
      return
    }
    const touch = event.touches[0]
    this.startX = touch.clientX
    this.startY = touch.clientY
    this.lastX = touch.clientX
    this.lastT = event.timeStamp

    this.hasPrev = !!this.navEl("prev")
    this.hasNext = !!this.navEl("next")
    this.dragEls = this.contentElements()
    this.tracking = true
    this.dragging = false
  }

  handleTouchMove(event) {
    if (!this.tracking || event.touches.length !== 1) return
    const touch = event.touches[0]
    const deltaX = touch.clientX - this.startX
    const deltaY = touch.clientY - this.startY

    if (!this.dragging) {
      if (Math.abs(deltaX) < INTENT_PX && Math.abs(deltaY) < INTENT_PX) return
      if (Math.abs(deltaY) >= Math.abs(deltaX)) {
        // Vertical intent — let the page scroll, abandon the gesture.
        this.tracking = false
        return
      }
      this.dragging = true
      for (const el of this.dragEls) el.style.transition = "none"
    }

    event.preventDefault() // we own this horizontal gesture now
    this.lastX = touch.clientX
    this.lastT = event.timeStamp

    // Rubber-band when dragging toward an edge with no neighbour.
    const hasTarget = this.wantsNext(deltaX) ? this.hasNext : this.hasPrev
    const offset = hasTarget
      ? deltaX
      : Math.sign(deltaX) * Math.min(Math.abs(deltaX) * RUBBER, RUBBER_CAP)
    const opacity = 1 - Math.min(Math.abs(offset) / FADE_OVER_PX, 0.3)
    for (const el of this.dragEls) {
      el.style.transform = `translateX(${offset}px)`
      el.style.opacity = String(opacity)
    }
  }

  handleTouchEnd(event) {
    if (!this.tracking) return
    const wasDragging = this.dragging
    this.tracking = false
    this.dragging = false
    if (!wasDragging) {
      this.resetDrag()
      return
    }

    const touch = event.changedTouches[0]
    const deltaX = touch.clientX - this.startX
    const dt = Math.max(event.timeStamp - this.lastT, 1)
    const velocity = (touch.clientX - this.lastX) / dt

    const hasTarget = this.wantsNext(deltaX) ? this.hasNext : this.hasPrev
    const flick = Math.abs(velocity) > FLICK_VELOCITY && Math.abs(deltaX) > FLICK_MIN_PX
    const commit = hasTarget && (Math.abs(deltaX) > COMMIT_PX || flick)

    if (commit) {
      this.commitDrag(deltaX)
    } else {
      this.snapBack()
    }
  }

  handleTouchCancel() {
    if (!this.tracking) return
    this.tracking = false
    this.dragging = false
    this.snapBack()
  }

  commitDrag(deltaX) {
    const link = this.navEl(this.wantsNext(deltaX) ? "next" : "prev")
    if (!link) {
      this.snapBack()
      return
    }

    const sign = Math.sign(deltaX) || -1
    const els = this.dragEls || this.contentElements()
    if (this.reduceMotion) {
      this.clearInlineStyles(els)
    } else {
      for (const el of els) {
        if (typeof el.animate !== "function") continue
        // Continue the outgoing question off in the same direction, then it gets
        // replaced when the new frame renders.
        el.animate(
          [
            { transform: el.style.transform || "translateX(0)", opacity: el.style.opacity || "1" },
            { transform: `translateX(${sign * OUT_PX}px)`, opacity: 0 }
          ],
          { duration: OUT_MS, easing: "ease-in", fill: "forwards" }
        )
      }
    }

    this.navigating = true
    // Cleared by turbo:frame-load; fall back in case the click leaves the frame.
    this.navigateTimeout = setTimeout(() => { this.navigating = false }, 1500)
    this.dragEls = null
    this.fromDrag = true
    link.click() // routes through handleClick, which records the slide direction
    this.fromDrag = false
  }

  snapBack() {
    const els = this.dragEls || this.contentElements()
    this.dragEls = null
    for (const el of els) {
      const from = el.style.transform
      if (this.reduceMotion || !from || from === "translateX(0px)") {
        this.clearInlineStyles([el])
        continue
      }
      if (typeof el.animate !== "function") {
        this.clearInlineStyles([el])
        continue
      }
      const anim = el.animate(
        [
          { transform: from, opacity: el.style.opacity || "1" },
          { transform: "translateX(0)", opacity: 1 }
        ],
        { duration: SNAP_MS, easing: EASE_OUT }
      )
      const done = () => this.clearInlineStyles([el])
      anim.finished.then(done).catch(done)
    }
  }

  clearInlineStyles(els) {
    for (const el of els) {
      el.style.transform = ""
      el.style.opacity = ""
      el.style.transition = ""
    }
  }

  // The actionable Back/Next control for a direction, or null when it's absent
  // (edge of the test) or disabled (Next before the question is answered).
  navEl(direction) {
    const role = direction === "next" ? "next" : "prev"
    const el = this.element.querySelector(`[data-nav-role="${role}"]`)
    if (!el || el.disabled) return null
    return el
  }

  // Record which way we're travelling for the slide-in. Only the synthetic click
  // fired by commitDrag qualifies: the slide exists to continue the gesture the
  // user just made. Taps, keyboard Enter and the programmatic Next click after an
  // answer repeat dozens of times per session, so they swap instantly instead.
  // Links that leave the frame (Finish / _top) are skipped — full-page visits.
  handleClick(event) {
    if (!this.fromDrag) return
    const el = event.target.closest("a, button[data-nav-role]")
    if (!el || !this.element.contains(el)) return
    if (el.dataset.turbo === "false" || el.dataset.turboFrame === "_top" || el.target === "_top") return
    this.direction = this.directionTo(el)
  }

  directionTo(el) {
    const roleEl = el.closest("[data-nav-role]")
    if (roleEl) return roleEl.dataset.navRole === "prev" ? "prev" : "next"

    // A grid cell jump — compare its index against the current question.
    const cell = el.closest("[data-cell-index]")
    if (cell) {
      const root = this.element.querySelector("[data-nav-current-index]")
      const cur = root ? parseInt(root.dataset.navCurrentIndex, 10) : 0
      const tgt = parseInt(cell.dataset.cellIndex, 10)
      if (!Number.isNaN(tgt)) return tgt < cur ? "prev" : "next"
    }
    return "next"
  }

  handleFrameRender() {
    if (this.direction) {
      this.slideIn(this.direction)
      this.direction = null
    }
  }

  // Slide the new question content into place. Physical direction follows the
  // progression: next enters from the leading edge, previous from the trailing
  // edge (flipped under RTL), matching the drag the user just made.
  slideIn(direction) {
    if (this.reduceMotion) return
    const fromRight = (direction === "next") !== this.isRtl
    const dx = fromRight ? SLIDE_IN_PX : -SLIDE_IN_PX
    for (const el of this.contentElements()) {
      if (typeof el.animate !== "function") continue
      el.animate(
        [
          { transform: `translateX(${dx}px)`, opacity: 0 },
          { transform: "translateX(0)", opacity: 1 }
        ],
        { duration: SLIDE_IN_MS, easing: EASE_OUT }
      )
    }
  }

  handleFrameLoad() {
    this.navigating = false
    if (this.navigateTimeout) clearTimeout(this.navigateTimeout)
  }
}
